import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Camera, UserCircle, Calendar, Users, FileBarChart, LogOut, Sun, Moon, Activity, Megaphone } from 'lucide-react';

const Navigation = ({ user, onLogout }) => {
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr' || user?.role === 'HR';
  const isHOD = Boolean(user?.is_hod || String(user?.position || '').toUpperCase() === 'HOD');
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  const employeeLinks = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/punch', label: 'Punch', icon: Camera },
    { path: '/leaves', label: isHOD ? 'Leaves' : 'Leaves', icon: Calendar },
    { path: '/profile', label: 'Profile', icon: UserCircle },
  ];
  
  const hrLinks = [
    { path: '/', label: 'My Dashboard', icon: LayoutDashboard },
    { path: '/punch', label: 'Punch', icon: Camera },
    { path: '/leaves', label: 'Leaves', icon: Calendar },
    { path: '/admin', label: "Today's Attendance", icon: Activity }, 
    { path: '/users', label: 'Directory', icon: Users },
    { path: '/reports', label: 'Reports', icon: FileBarChart },
    { path: '/profile', label: 'Profile', icon: UserCircle },
  ];

  const adminLinks = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/profile', label: 'Profile', icon: UserCircle },
    { path: '/users', label: 'Users', icon: Users },
    { path: '/reports', label: 'Reports', icon: FileBarChart },
    { path: '/announcements', label: 'Announcements', icon: Megaphone },
  ];

  const navLinks = isAdmin ? adminLinks : isHR ? hrLinks : employeeLinks;

  const NavLink = ({ path, label, icon: Icon, disabled }) => {
    const active = location.pathname === path;
    const handleClick = (e) => {
      if (disabled) {
        e.preventDefault(); 
        alert("This function is disabled for now. It will open soon.");
      }
    };

    return (
      <Link 
        to={path}
        onClick={handleClick}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 shrink-0 ${
          disabled 
            ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800/50' 
            : active 
              ? 'bg-slate-900 text-white dark:bg-emerald-600' 
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
        }`}
        data-testid={`nav-link-${label.toLowerCase()}`}>
        <Icon className={`w-4 h-4 ${disabled ? 'opacity-50' : ''}`} />
        <span className="hidden sm:inline">{label}</span>
      </Link>
    );
  };

  return (
    <nav className="bg-white border-b border-zinc-200 dark:bg-slate-900 dark:border-slate-800 transition-colors duration-200" data-testid="main-navigation">
      <style>{`
        .nav-scroll::-webkit-scrollbar { display: none; }
        .nav-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between min-h-[3.5rem] py-2 gap-2 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-6 flex-1 min-w-0">
            <Link to="/" className="flex items-center gap-2.5 shrink-0 max-w-[200px] sm:max-w-[280px] xl:max-w-none">
              <img src="/logo.png" alt="Logo" className="w-7 h-7 object-contain rounded-sm shrink-0" 
                   onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
              <span className="font-bold text-slate-900 dark:text-white text-sm sm:text-base truncate transition-colors" style={{fontFamily:'Outfit'}} data-testid="nav-brand">
                NS Group Attendance Management
              </span>
            </Link>

            <div className="h-5 w-px bg-zinc-200 dark:bg-slate-700 hidden lg:block transition-colors shrink-0" />

            <div className="hidden lg:flex items-center gap-1 flex-1 overflow-x-auto nav-scroll">
              {navLinks.map(link => <NavLink key={link.path} {...link} />)}
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <button 
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors shrink-0"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <div className="text-right hidden sm:block max-w-[120px] lg:max-w-[180px] xl:max-w-[260px]">
              <p className="text-sm font-medium text-slate-900 dark:text-white leading-tight truncate transition-colors" data-testid="nav-user-name">
                {user?.full_name || user?.username}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 truncate transition-colors capitalize">
                {user?.department} {isHOD ? '• HOD' : ''}
              </p>
            </div>

            <div className="h-5 w-px bg-zinc-200 dark:bg-slate-700 transition-colors shrink-0" />
            
            <button onClick={onLogout}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 dark:text-slate-400 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-all duration-200 shrink-0"
              data-testid="logout-button">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>
      
      <div className="lg:hidden flex items-center gap-1 overflow-x-auto nav-scroll px-4 pb-2 bg-white dark:bg-slate-900 border-b border-zinc-200 dark:border-slate-800">
         {navLinks.map(link => <NavLink key={link.path} {...link} />)} 
      </div>
    </nav>
  );
};

export default Navigation;